import React from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "orange";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  isLoading = false,
  disabled,
  fullWidth = false,
  startIcon,
  endIcon,
  className = "",
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center font-medium text-nowrap rounded-[8px] transition-colors duration-500 focus:outline-none disabled:cursor-not-allowed";

  const variantStyles: Record<ButtonVariant, string> = {
    primary: "bg-primary text-white hover:enabled:bg-primary/90",
    secondary: "bg-secondary text-[#141212] hover:enabled:bg-secondary/80",
    ghost:
      "bg-[#EAEAEA] text-black hover:enabled:bg-[#F5F5F6] hover:enabled:text-black",
    orange:
      "bg-orange text-white hover:enabled:bg-[#F5F5F6] hover:enabled:text-black",
  };

  const sizeStyles: Record<ButtonSize, string> = {
    sm: "px-4 text-xs h-[36px] rounded-md",
    md: "px-8 h-[36px] text-sm",
    lg: "px-6 py-3 text-lg",
  };

  const widthStyle = fullWidth ? "w-full" : "";

  // Apply opacity only when disabled and NOT loading
  const opacityStyle = disabled && !isLoading ? "opacity-20" : "";

  const finalClassName = [
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    widthStyle,
    opacityStyle,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      className={finalClassName}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span
          className="inline-block w-4 h-4 mr-2 border-2 border-white/60 border-t-white rounded-full animate-spin align-middle"
          aria-hidden="true"
        />
      )}

      {/* Start Icon */}
      {!isLoading && startIcon && (
        <span className="mr-2 flex items-center">{startIcon}</span>
      )}

      {children}

      {/* End Icon */}
      {!isLoading && endIcon && (
        <span className="ml-2 flex items-center">{endIcon}</span>
      )}
    </button>
  );
};

export default Button;
